/**
 * `/templates` — the registry, and `/templates/:documentType` — the editor.
 *
 * The registry answers the two questions the office actually asks about a
 * template: how much of this document did we draft with no sample behind it,
 * and how much of it looks statutory with nobody having confirmed it. Both are
 * numbers the server computes, so this screen and the load report cannot
 * disagree.
 *
 * The editor's whole posture is in one rule: **`[S]` has no editing affordance
 * at all.** Not a disabled textarea an officer might think is a bug — a lock, a
 * sentence saying where statutory text comes from, and no control. The API
 * refuses it too; this is the half that means nobody tries.
 */

import { useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type {
  BlockPreview,
  DifferenceView,
  TemplateRow,
  LibraryEntryView,
  SharedTextView,
  DocumentContext,
  PolishProposal,
  ProvenanceAction,
  UnresolvedSlotView,
  TemplateBlockView,
  ParagraphKindView,
  ParagraphStyleView,
  TemplateCheckView,
  TemplateDetail,
} from '../api/client'
import {
  ConditionBuilder,
  LayerBadge,
  PublishBox,
  ValidationReport,
} from '../components/editor'
import { ErrorEnvelope } from '../components/bits'
import {
  GapsPanel,
  ParagraphKindPicker,
  ParagraphStylePicker,
} from '../components/ParagraphKind'
import { Palette, SplitEditor } from '../components/SplitEditor'
import { readDay } from '../lib/blocks'

/**
 * Share this factory's template with the other eight.
 *
 * **The review step is the feature, not the button.** A template is office
 * procedure only if nothing in it names a case, and an instruction reasonably
 * written as *"for example, as in the ⟨name⟩ matter…"* carries a real
 * employee's name across a boundary that is otherwise absolute.
 *
 * Ruled 30 Aug 2026, and the ruling was explicit about which kind of answer
 * this is: *"not a scan, not a guard — the text, in front of the officer,
 * before it goes."* No pattern-matcher can be trusted to spot a name; a name is
 * just words. So every word goes on screen and a person decides.
 */
function PublishToLibrary({ detail }: { detail: TemplateDetail }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [shared, setShared] = useState<LibraryEntryView | null>(null)

  const text = useQuery({
    queryKey: ['shared-text', detail.document_type],
    queryFn: () => api.readSharedText(detail.document_type),
    enabled: open,
  })
  const publish = useMutation({
    mutationFn: () => api.publishToLibrary(detail.document_type, { preflight: false, note }),
    onSuccess: (entry: LibraryEntryView) => {
      setShared(entry)
      setOpen(false)
      setNote('')
      setConfirmed(false)
    },
  })

  // Only a published version goes, and only this factory's own. The
  // organisation-wide templates already reach every factory.
  if (!detail.ours || detail.status !== 'PUBLISHED') return null

  if (shared) {
    return (
      <p className="small good-text" data-testid="shared-confirmation">
        Shared with the other factories as version {shared.version}. They will see it in the
        library and may take a copy; nothing they do to their copy reaches this one.
      </p>
    )
  }
  if (!open) {
    return (
      <button type="button" className="quiet" onClick={() => setOpen(true)}>
        Share this with the other factories
      </button>
    )
  }
  return (
    <section className="card" data-testid="publish-to-library">
      <header>
        <h2>What the other factories would see</h2>
      </header>
      <div className="body">
        <p className="small warn-text">
          Read this before sharing. A template is office procedure only if nothing in it names a
          case. An instruction written as <em>&ldquo;as in the ⟨name⟩ matter&rdquo;</em>
          carries a real employee&rsquo;s name to eight other factories, and nothing here scans
          for one — a name is just words. <strong>You are the check.</strong>
        </p>
        {text.isPending ? <p className="small muted">Reading it…</p> : null}
        <div className="shared-text">
          {(text.data ?? []).map((line: SharedTextView, i: number) => (
            <div key={i} className="line">
              <span className="small faint mono">{line.where}</span>
              <pre className="deva scaffold">{line.text}</pre>
            </div>
          ))}
          {text.data && text.data.length === 0 ? (
            <p className="small muted">Nothing written into it yet.</p>
          ) : null}
        </div>
        <label className="small" style={{ marginTop: 10, display: 'block' }}>
          What is this, and why would another office want it?
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="small faint">
          Twenty characters at least. It is the only thing another factory has to go on.
        </p>
        <label className="small" style={{ display: 'block', marginTop: 6 }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="confirm-no-case-material"
          />{' '}
          I have read the text above and it names no case, no employee and no file number.
        </label>
        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={!confirmed || note.trim().length < 20 || publish.isPending}
            onClick={() => publish.mutate()}
          >
            {publish.isPending ? 'Sharing…' : 'Share it'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
        <ErrorEnvelope error={publish.error} />
      </div>
    </section>
  )
}

/**
 * What the nine factories have shared with each other.
 *
 * **Adopt by copying — never a live link.** A live link would reproduce exactly
 * the hazard the unit column removed: one factory's edit changing what another
 * generates, with more steps in the way. A copy arrives as a DRAFT, because the
 * adopting office has not read it yet.
 *
 * A factory sees its own entries beside everyone else's, which is how it knows
 * what it has shared.
 */
function Library() {
  const navigate = useNavigate()
  const entries = useQuery({ queryKey: ['library'], queryFn: () => api.listLibrary() })
  const adopt = useMutation({
    mutationFn: (entryId: string) =>
      api.adoptFromLibrary({ preflight: false, entry_id: entryId }),
    onSuccess: (row: TemplateDetail) => navigate(`/templates/${row.document_type}`),
  })

  const rows = entries.data ?? []
  return (
    <section className="card">
      <header>
        <h2>The library — what the factories have shared</h2>
      </header>
      <div className="body">
        <p className="small muted">
          A template another office wrote and offered. Taking one gives you a{' '}
          <strong>copy, as your own draft</strong> — it is yours from that moment, and nothing
          you do to it reaches the office it came from, or the other way round.
        </p>
        {rows.length === 0 ? (
          <p className="small faint" data-testid="library-empty">
            Nothing shared yet. When a factory publishes one of its own documents here, every
            other factory can take a copy.
          </p>
        ) : null}
        {rows.map((row: LibraryEntryView) => (
          <div className="library-entry" key={row.id} data-testid={`library-${row.id}`}>
            <div>
              <strong>{row.title}</strong>{' '}
              <span className="small muted">
                from {row.from_unit} · v{row.version} · {row.blocks} blocks
              </span>
              <p className="small">{row.note}</p>
              <p className="small faint">
                Published by {row.published_by} on {readDay(row.published_at)}
              </p>
            </div>
            {row.ours ? (
              <span className="chip neutral">Ours</span>
            ) : (
              <button
                type="button"
                className="quiet"
                disabled={adopt.isPending}
                onClick={() => adopt.mutate(row.id)}
              >
                Take a copy
              </button>
            )}
          </div>
        ))}
        <ErrorEnvelope error={entries.error} />
        <ErrorEnvelope error={adopt.error} />
      </div>
    </section>
  )
}

/**
 * This factory's own documents, listed where the officer looks for them.
 *
 * They were reachable only by scrolling a list of twenty-two and picking
 * `X-FOLLOW-UP-LETTER-TO-A-CONTRACT` out of the twenty statutory ones — which
 * is how an officer came to ask where the Share button was, 31 Aug 2026. It was
 * inside each template's own page, and there was no path to that page from
 * here.
 *
 * Each row says what state it is in, because the three states need three
 * different acts: nothing written yet, written but not published, published —
 * and only the last can be shared.
 */
function OurDocuments({ rows, shared }: { rows: TemplateRow[]; shared: Set<string> }) {
  const ours = rows.filter((r) => r.ours)
  if (ours.length === 0) return null
  return (
    <div className="our-documents">
      {ours.map((row) => {
        const published = row.published_version != null
        const isShared = shared.has(row.document_type)
        return (
          <div
            className="our-document"
            key={row.document_type}
            data-testid={`ours-${row.document_type}`}
          >
            <div>
              <Link to={`/templates/${row.document_type}`}>
                <strong>{row.document_type}</strong>
              </Link>
              <span className="small muted">
                {' '}
                · {row.blocks} block{row.blocks === 1 ? '' : 's'}
              </span>
              <div className="small faint">
                {!published && row.blocks === 0
                  ? 'Nothing written into it yet — open it and add its blocks.'
                  : !published
                    ? 'Written but not published. Publish it and it can be generated on a case.'
                    : isShared
                      ? 'Published, and shared with the other factories.'
                      : 'Published. Open it to share it with the other factories.'}
              </div>
            </div>
            <Link className="quiet" to={`/templates/${row.document_type}`}>
              {published && !isShared ? 'Open and share' : 'Open'}
            </Link>
          </div>
        )
      })}
    </div>
  )
}

/**
 * A document outside the twenty.
 *
 * Ruled 30 Aug 2026 (design doc point 6). Officers write correspondence every
 * day — follow-up letters, reminders to a contractor, covering letters — and
 * the product helped with none of it, because the twenty is a closed list: a
 * case's rail comes from the pinned process version, and a template for a
 * follow-up letter would appear on no case, have no instance and offer nowhere
 * to press Generate.
 *
 * The officer writes what they call it and the code is derived. Asking them to
 * invent `X-FOLLOW` is asking them to do the database's job.
 *
 * It is **this factory's from the first moment** — unlike the twenty, which are
 * seeded organisation-wide. It reaches another factory only by being published
 * to the library and adopted there, which is a copy and a deliberate act.
 */
function NewDocument() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [titleHi, setTitleHi] = useState('')
  const create = useMutation({
    mutationFn: () =>
      api.createAdhocTemplate({ preflight: false, title, title_hi: titleHi } as never),
    onSuccess: (row: TemplateDetail) => {
      setOpen(false)
      setTitle('')
      setTitleHi('')
      // Straight into the editor. A document type with no blocks is not a
      // thing anybody wants to look at in a list.
      navigate(`/templates/${row.document_type}`)
    },
  })

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="new-document">
        + A document of our own
      </button>
    )
  }
  return (
    <div className="card" data-testid="new-document-form">
      <div className="body">
        <p className="small faint">
          Anything this office writes that is not one of the twenty — a follow-up letter, a
          reminder to a contractor, a covering letter. It belongs to this factory only, and you
          author it the same way you author any template.
        </p>
        <label className="small">
          What do you call it?
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Follow-up letter to a contractor"
            data-testid="new-document-title"
          />
        </label>
        <label className="small">
          And in Hindi, as it should read on the document
          {/* No Devanagari placeholder. The screen is English and the check in
              the build enforces it — an example in a placeholder is a LABEL,
              and the allowlist is for record identity, not labels. The field's
              own label already says what it wants. */}
          <input
            className="deva"
            value={titleHi}
            onChange={(e) => setTitleHi(e.target.value)}
            data-testid="new-document-title-hi"
          />
        </label>
        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create it and start writing'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
        <ErrorEnvelope error={create.error} />
      </div>
    </div>
  )
}

export function Templates() {
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.listTemplates() })
  const library = useQuery({ queryKey: ['library'], queryFn: () => api.listLibrary() })

  const pendingSignoff = (templates.data ?? []).reduce((sum, t) => sum + t.pending_signoff, 0)

  return (
    <div className="page">
      <header>
        <h1>Templates</h1>
        <p className="small muted">
          Nineteen document types and the organisation&rsquo;s standing text. Everything is
          versioned; a published version is frozen and editing forks a draft.
        </p>
      </header>

      <section className="card">
        <header>
          <h2>Our own documents</h2>
        </header>
        <div className="body">
          <p className="small muted">
            The nineteen come from the Template Library and are the organisation&rsquo;s. A
            document this factory writes for itself — correspondence, a covering letter — is
            created here and belongs to this factory alone.
          </p>
          <OurDocuments
            rows={templates.data ?? []}
            shared={
              new Set(
                (library.data ?? []).filter((e) => e.ours).map((e: LibraryEntryView) => e.code),
              )
            }
          />
          <NewDocument />
        </div>
      </section>

      <Library />

      <section className="card">
        <header>
          <h2>The nineteen</h2>
          <span className="small muted">
            {pendingSignoff} block{pendingSignoff === 1 ? '' : 's'} awaiting the Template
            administrator&rsquo;s sign-off
          </span>
        </header>
        <div className="body">
          {templates.isLoading ? <p className="muted">Reading the registry…</p> : null}
          <ErrorEnvelope error={templates.error} />
          <table className="grid">
            <thead>
              <tr>
                <th>Document</th>
                <th>Published</th>
                <th>Draft</th>
                <th>Blocks</th>
                <th>Rules</th>
              </tr>
            </thead>
            <tbody>
              {(templates.data ?? []).map((row) => (
                <tr key={row.document_type}>
                  <td className="mono">
                    <Link to={`/templates/${row.document_type}`}>{row.document_type}</Link>
                  </td>
                  <td className="mono">{row.published_version ?? '—'}</td>
                  <td className="mono">{row.draft_version ?? '—'}</td>
                  <td className="mono">{row.blocks}</td>
                  <td className="mono">{row.checks}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="small faint">
            D12 is not listed, and that is correct rather than an omission: the Inquiry Report
            is written by the Inquiry Officer, who must apply their own mind, and has no
            template (ADR-021).
          </p>
        </div>
      </section>

    </div>
  )
}

export function OrgBlocks({ unconfirmed }: { unconfirmed: number }) {
  const client = useQueryClient()
  const blocks = useQuery({ queryKey: ['org-blocks'], queryFn: () => api.listOrgBlocks() })
  const confirm = useMutation({
    mutationFn: (id: string) => api.confirmOrgBlock(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['org-blocks'] }),
  })

  return (
    <section className="card">
      <header>
        <h2>Organisation blocks</h2>
        <span className="small muted">
          {unconfirmed} awaiting the office&rsquo;s confirmation
        </span>
      </header>
      <div className="body">
        <p className="small muted">
          Text authored once for the organisation and referenced by every template — the
          letterhead, the signature block, the addressee stack. Publishing one affects{' '}
          <strong>future generations only</strong>.
        </p>
        <ErrorEnvelope error={confirm.error} />
        <table className="grid">
          <thead>
            <tr>
              <th>Key</th>
              <th>Version</th>
              <th>Status</th>
              <th>Confirmed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(blocks.data ?? []).map((block) => (
              <tr key={block.id}>
                <td className="mono">{block.key}</td>
                <td className="mono">{block.version}</td>
                <td>{block.status}</td>
                <td>
                  {block.confirmed ? (
                    <span className="small">
                      {block.confirmed_by}
                      {block.confirmed_at ? ` · ${readDay(block.confirmed_at)}` : ''}
                    </span>
                  ) : (
                    <span className="chip warn">pending confirmation</span>
                  )}
                </td>
                <td>
                  {block.status === 'PUBLISHED' && !block.confirmed ? (
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => confirm.mutate(block.id)}
                      disabled={confirm.isPending}
                    >
                      Confirm with the office
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small faint">
          Extracted from the newest real samples, which is evidence rather than confirmation.
          Until the office signs one off, every document generated from it carries office text
          nobody has confirmed is current — and the officer can see that.
        </p>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------ the editor */

export function TemplateEditor() {
  const { documentType = '' } = useParams()
  const client = useQueryClient()
  const [tab, setTab] = useState<'blocks' | 'slots' | 'rules'>('blocks')
  // Which language is being edited. Hindi is the source and the default; the
  // tabs switch it, and every query and command below keys on it — editing the
  // English forks the English and leaves the Hindi untouched.
  const [language, setLanguage] = useState<'hi' | 'en'>('hi')

  const template = useQuery({
    queryKey: ['template', documentType, language],
    queryFn: () => api.getTemplate(documentType, language),
  })
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['template', documentType] })
    void client.invalidateQueries({ queryKey: ['template', documentType, language] })
    void client.invalidateQueries({ queryKey: ['templates'] })
    void client.invalidateQueries({ queryKey: ['template-diff', documentType] })
  }

  const openDraft = useMutation({
    mutationFn: () => api.openTemplateDraft(documentType, language),
    onSuccess: invalidate,
  })
  const publish = useMutation({
    mutationFn: (change_note: string) =>
      api.publishTemplate(documentType, { preflight: false, change_note }),
    onSuccess: invalidate,
  })
  const discard = useMutation({
    mutationFn: () => api.discardTemplateDraft(documentType),
    onSuccess: invalidate,
  })

  const detail = template.data
  const isDraft = detail?.status === 'DRAFT'
  const errors = (detail?.problems ?? []).filter((p) => p.level === 'error')

  // **A warning about a paragraph belongs on the paragraph.** The server says
  // where in the one form it has — `where: "block 9"` — so it is read back
  // here rather than a field being added to the API for a placement decision.
  // Anything whose `where` is not a block still reaches the publish card as an
  // error, or is dropped as a warning about nothing locatable.
  const warningsByBlock = new Map<number, string[]>()
  for (const problem of detail?.problems ?? []) {
    if (problem.level === 'error') continue
    const at = /^block (\d+)$/.exec(problem.where ?? '')
    if (!at) continue
    const order = Number(at[1])
    warningsByBlock.set(order, [...(warningsByBlock.get(order) ?? []), problem.message])
  }

  return (
    <div className="page">
      <header>
        <h1>
          <Link to="/templates" className="quiet-link">
            Templates
          </Link>{' '}
          / <span className="mono">{documentType}</span>
        </h1>
        {detail ? (
          <p className="small muted">
            Version <span className="mono">{detail.version}</span> ·{' '}
            <span className={`chip ${isDraft ? 'warn' : 'good'}`}>{detail.status}</span>
            {detail.source_file ? (
              <>
                {' '}
                · read from <span className="mono">{detail.source_file}</span>
              </>
            ) : null}
          </p>
        ) : null}
      </header>

      {detail ? (
        <LanguageTabs
          detail={detail}
          language={language}
          onPick={setLanguage}
          onChanged={invalidate}
        />
      ) : null}

      {detail ? (
        <RenameDocument detail={detail} editable={isDraft} onChanged={invalidate} />
      ) : null}

      {/* **This factory's own copy.** The twenty are seeded organisation-wide;
          the first edit a factory makes forks the template to them, so an edit
          here reaches nobody else. Worth saying plainly: an officer who thinks
          they are editing the organisation's template will write a change note
          for an audience that will never read it. */}
      {detail?.ours ? (
        <p className="small muted" data-testid="ours">
          This is <strong>your factory&rsquo;s own copy</strong> of{' '}
          <span className="mono">{detail.document_type}</span>. Editing it changes nothing at
          any other factory.
        </p>
      ) : null}

      {/* A flag, never a sync. Re-copying the organisation's change over the
          top would be changing a template under the officer who owns it. */}
      {detail?.org_wide_drift ? (
        <p className="small warn-text" data-testid="org-wide-drift">
          {detail.org_wide_drift}
        </p>
      ) : null}

      {/* Tells, never updates — the same rule as every other drift in this
          product. The adopting factory owns its copy. */}
      {detail?.library_drift ? (
        <p className="small warn-text" data-testid="library-drift">
          {detail.library_drift}
        </p>
      ) : null}

      {/* The third drift, built the same way as the other two: the copy belongs
          to the officer who owns it, so this tells and never re-translates. */}
      {detail?.language_drift ? (
        <p className="small warn-text" data-testid="language-drift">
          {detail.language_drift}
        </p>
      ) : null}

      {detail ? <PublishToLibrary detail={detail} /> : null}

      {/* **At the top, above the diff.** It was mounted after the publish card
          and landed twelve thousand pixels down a template with a long draft
          diff — present, and unfindable. Reading the office's own documents is
          the FIRST thing anybody does with a template, not the last. */}
      {detail ? (
        <LearnFromDocuments
          documentType={documentType}
          editable={isDraft}
          empty={(detail.blocks ?? []).length === 0}
          onChanged={invalidate}
        />
      ) : null}

      <ErrorEnvelope error={template.error} />
      <ErrorEnvelope error={openDraft.error} />
      <ErrorEnvelope error={publish.error} />

      {detail && !isDraft ? (
        <section className="card">
          <div className="body">
            <p className="small muted">
              This version is published and therefore frozen — cases have documents pinned to
              it. Editing forks a draft.
            </p>
            <button
              type="button"
              onClick={() => openDraft.mutate()}
              disabled={openDraft.isPending}
            >
              {openDraft.isPending ? 'Opening…' : 'Edit — opens a draft'}
            </button>
          </div>
        </section>
      ) : null}

      {detail ? (
        <AboutThisDocument
          documentType={documentType}
          context={detail.context}
          editable={isDraft}
          onChanged={invalidate}
        />
      ) : null}

      {detail ? (
        <>
          <nav className="tabs">
            <button
              type="button"
              className={tab === 'blocks' ? 'active' : ''}
              onClick={() => setTab('blocks')}
            >
              Blocks ({(detail.blocks ?? []).length})
            </button>
            <button
              type="button"
              className={tab === 'slots' ? 'active' : ''}
              onClick={() => setTab('slots')}
            >
              Slots ({(detail.slots ?? []).length})
            </button>
            <button
              type="button"
              className={tab === 'rules' ? 'active' : ''}
              onClick={() => setTab('rules')}
            >
              Pre-issue rules ({(detail.checks ?? []).length})
            </button>
          </nav>

          {tab === 'blocks' ? (
            <Blocks
              detail={detail}
              editable={isDraft}
              onChanged={invalidate}
              language={language}
              warningsByBlock={warningsByBlock}
            />
          ) : null}
          {tab === 'slots' ? (
            <Slots detail={detail} editable={isDraft} onChanged={invalidate} />
          ) : null}
          {tab === 'rules' ? (
            <Rules detail={detail} editable={isDraft} onChanged={invalidate} />
          ) : null}
        </>
      ) : null}

      {/* **Publishing is the last act, so it is the last thing on the page.**
          It used to sit above the document, which asked the officer to decide
          whether to publish a template he had not yet been shown.

          Only errors are reported here. A warning is not a bar to publishing
          and has no business in the publish gate — it is a fact about one
          paragraph, and it is now shown on that paragraph, beside the panel
          that fixes it. Four identical banners naming "block 9, block 10"
          sent the officer hunting for paragraphs the screen would not show
          him. */}
      {detail && isDraft ? (
        <section className="card" data-testid="publish">
          <header>
            <h2>Publish</h2>
          </header>
          <div className="body">
            <ValidationReport
              problems={errors}
              emptyText="Nothing outstanding. This draft is ready to publish."
            />
            <WhatThisDraftChanges documentType={documentType} />
            <PublishBox
              label="Publish this version"
              busy={publish.isPending}
              disabled={errors.length > 0}
              disabledReason={
                errors.length
                  ? `${errors.length} validation problem${errors.length === 1 ? '' : 's'} must be fixed first. The button stays here so the rule is visible.`
                  : undefined
              }
              onPublish={(note) => publish.mutate(note)}
            />
            <p className="small faint">
              Publishing affects <strong>new documents only</strong>. Every document already
              generated is pinned to the template version that produced it, and its
              click-through still leads to that version, unchanged.
            </p>
            <button type="button" className="quiet" onClick={() => discard.mutate()}>
              Discard this draft
            </button>
          </div>
        </section>
      ) : null}
    </div>
  )
}

/**
 * Rename a document this office wrote for itself.
 *
 * The name changes; the **code never does**. A typed name is a label and an
 * officer who wrote *Remainder* meaning *Reminder* must be able to fix it,
 * but the code is identity — it is on every instance, every audit row and
 * every URL, and rewriting it would orphan documents already generated. The
 * code is shown beside the field so nobody expects otherwise.
 *
 * Offered only on a document outside the twenty. The twenty are named by the
 * process definition, which is where they are edited and versioned; a second
 * place to name them would be a second truth.
 */
function RenameDocument({
  detail,
  editable,
  onChanged,
}: {
  detail: TemplateDetail
  editable: boolean
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(detail.title)
  const [titleHi, setTitleHi] = useState(detail.title_hi)
  const rename = useMutation({
    mutationFn: () =>
      api.renameAdhocTemplate(detail.document_type, {
        preflight: false,
        title,
        title_hi: titleHi,
      } as never),
    onSuccess: () => {
      setOpen(false)
      onChanged()
    },
  })

  if (!detail.adhoc) return null
  if (!open) {
    return (
      <p className="small muted">
        <strong>{detail.title}</strong>
        {editable ? (
          <>
            {' '}
            <button type="button" className="quiet" onClick={() => setOpen(true)}>
              Rename
            </button>
          </>
        ) : null}
      </p>
    )
  }
  return (
    <div className="card" data-testid="rename-document">
      <div className="body">
        <label className="small">
          What do you call it?
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="small">
          And in Hindi, as it should read on the document
          <input
            className="deva"
            value={titleHi}
            onChange={(e) => setTitleHi(e.target.value)}
          />
        </label>
        <p className="small faint">
          The name changes. Its code <span className="mono">{detail.document_type}</span> does
          not — it is on every document already generated from this, and on every audit row.
        </p>
        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={!title.trim() || rename.isPending}
            onClick={() => rename.mutate()}
          >
            {rename.isPending ? 'Saving…' : 'Save the name'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
        <ErrorEnvelope error={rename.error} />
      </div>
    </div>
  )
}

/**
 * Build this template from real documents the office has already issued.
 *
 * Point 7, and the thing that makes **nine factories** possible rather than
 * one. The twenty seeded templates were read off Korwa's documents by hand over
 * weeks; correspondence has nothing, and nobody is going to read those by hand
 * nine times over.
 *
 * **Two or more, and the number is not a round one.** From one document you
 * cannot tell what is fixed and what varies — whether `कार्यालय टिप्पणी` is the
 * office's standing text or one clerk's habit is exactly what the comparison
 * answers. Two real documents were enough to show that the subject line's
 * `श्री …` and `… के संबंध में ।` are the office's and only the middle varies.
 * One showed nothing.
 *
 * The proposal lands in the draft the officer is already looking at. There is
 * no accept screen because there is nothing to accept onto: publishing is the
 * accept, discarding is the reject, and every block arrives carrying why it was
 * classified as it was, so the reasoning can be corrected and not just the
 * result.
 */
/** What makes two chosen files the same file.
 *
 * Name alone is not enough — two genuinely different documents can arrive under
 * the same name from different folders — and a `File` has no id. Name, size and
 * modification time together are what a browser can offer.
 */
function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

function LearnFromDocuments({
  documentType,
  editable,
  empty,
  onChanged,
}: {
  documentType: string
  editable: boolean
  empty: boolean
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [purpose, setPurpose] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const learn = useMutation({
    mutationFn: () => api.learnTemplate(documentType, purpose, files),
    onSuccess: () => {
      setOpen(false)
      setFiles([])
      onChanged()
    },
  })

  if (!editable) return null
  if (!open) {
    // A draft with no blocks is a document nobody has written yet, and reading
    // the office's own is the whole point of the screen — so it is the screen,
    // not a button somewhere on it.
    return empty ? (
      <section className="card" data-testid="learn-invitation">
        <header>
          <h2>Start from the office&rsquo;s own documents</h2>
        </header>
        <div className="body">
          <p className="small muted">
            Nothing has been written into this document yet. Give it{' '}
            <strong>two or more</strong> real ones of the same kind that this office has already
            issued, and it will work out the template behind them — what is the same in every
            one, what changes per case, and what comes off the case record.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => setOpen(true)}
            data-testid="learn"
          >
            Build this from real documents
          </button>
          <p className="small faint">
            Or write it block by block below. Reading real documents is faster and it is
            grounded in what the office actually issues.
          </p>
        </div>
      </section>
    ) : (
      // **A card, not one quiet line.** Reading the office's own documents is
      // the fastest way to a template that is actually theirs, and on a
      // template that already has paragraphs it was rendering as a bare link
      // between two headed cards — present, and passed straight over. It was
      // moved up the page once for the same reason; being high on the page
      // does not help if it does not look like anything.
      <section className="card" data-testid="learn-offer">
        <header>
          <h2>Build this from real documents</h2>
        </header>
        <div className="body">
          <p className="small muted">
            Give it two or more of these that the office has already issued, and it works out
            the template behind them — what is the same every time, what is written per case,
            and what comes off the case record.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => setOpen(true)}
            data-testid="learn"
          >
            Choose the documents
          </button>
        </div>
      </section>
    )
  }
  return (
    <section className="card" data-testid="learn-panel">
      <header>
        <h2>Read the office&rsquo;s own documents</h2>
      </header>
      <div className="body">
        <p className="small muted">
          Give it <strong>two or more</strong> real documents of this type that this office has
          already issued. It compares them: what is the same in every one is the office&rsquo;s
          standing text, what differs is written per case, and what differs but matches a field
          of the case record is a slot.
        </p>
        <p className="small faint">
          One document is not enough, and that is not a rule of thumb — from one you cannot tell
          the office&rsquo;s wording from one clerk&rsquo;s habit.
        </p>

        <label className="small">
          What is this document, in your own words?
          <textarea rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </label>
        <Polish text={purpose} mode="instruction" onAccept={setPurpose} />

        <label className="small" style={{ display: 'block', marginTop: 10 }}>
          The documents — Word files
          {/* **Adds, never replaces.** A file input hands back only the files
              chosen in THAT selection, so assigning them wholesale drops
              everything picked before — and this feature needs two or more, so
              anybody adding them one at a time could never get past one. Which
              is exactly what happened the first time it was used.

              The input's own value is cleared afterwards so choosing the same
              file again still fires a change; without that, removing a file and
              re-adding it silently does nothing. */}
          <input
            type="file"
            multiple
            accept=".docx,.txt,text/plain"
            data-testid="learn-files"
            onChange={(e) => {
              const chosen = Array.from(e.target.files ?? [])
              setFiles((have) => {
                const seen = new Set(have.map(fileKey))
                return [...have, ...chosen.filter((f) => !seen.has(fileKey(f)))]
              })
              e.target.value = ''
            }}
          />
        </label>
        <p className="small faint">
          Choose them together, or one at a time — they add up. A scan goes through{' '}
          <Link to="/ocr-tool">the OCR tool</Link> first and comes back a Word file, the same
          path the office already uses for case papers. This reads words, not images.
        </p>
        {files.length ? (
          <ul className="chosen-files" data-testid="learn-chosen">
            {files.map((file) => (
              <li key={fileKey(file)}>
                <span className="mono small">{file.name}</span>
                <button
                  type="button"
                  className="quiet"
                  title="Take this one out"
                  onClick={() =>
                    setFiles((have) => have.filter((f) => fileKey(f) !== fileKey(file)))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={files.length < 2 || !purpose.trim() || learn.isPending}
            onClick={() => learn.mutate()}
          >
            {learn.isPending ? 'Reading them…' : 'Read them and build the draft'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
        {files.length === 1 ? (
          <p className="small warn-text">One more at least. See above for why.</p>
        ) : null}
        <p className="small faint">
          This <strong>replaces</strong> what is in the draft. Discarding the draft puts the
          published version back.
        </p>
        <ErrorEnvelope error={learn.error} />
      </div>
    </section>
  )
}

/**
 * What this document is, who reads it, and how this office writes it.
 *
 * Ruled 30 Aug 2026 (design doc point 1). Until now a drafting call knew
 * *write this block* and nothing about the document the block sat in: drafting
 * D01's body, the model did not know it was writing a noting to the head of
 * the unit asking for a recorded decision. Everything it did know came from us
 * — the standing rules and whatever we had written into the instruction while
 * reading samples. Nothing in the product let the office say what the document
 * *is*.
 *
 * Three boxes and not one, because they answer different questions and are
 * edited at different times. Per document type, never office-wide: a noting
 * and a chargesheet are not written in the same register, are not read by the
 * same person, and do not have the same job.
 *
 * **Language-agnostic** — ruled explicitly. The officer writes in whichever
 * language they think in; nothing here is translated, and the model reads
 * either.
 */
/**
 * A textarea that is as tall as what is in it.
 *
 * These boxes hold a paragraph, not a line, and at `rows={2}` the office's own
 * writing was cut off mid-sentence with no sign that anything was missing — a
 * clipped instruction reads as a finished one, and the officer corrects a
 * sentence he cannot see the end of.
 *
 * Measured on every value change rather than on input, so accepting a
 * suggestion — which sets the value from outside — resizes the box too.
 * `useLayoutEffect` because a resize after paint is a visible jump.
 */
function GrowingTextarea({
  id,
  value,
  disabled,
  testid,
  onChange,
  minRows = 5,
}: {
  id?: string
  value: string
  disabled?: boolean
  testid: string
  onChange: (v: string) => void
  minRows?: number
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Collapse first: scrollHeight of an already-tall box never shrinks.
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  return (
    <textarea
      id={id}
      ref={ref}
      rows={minRows}
      className="grow"
      value={value}
      disabled={disabled}
      data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function AboutThisDocument({
  documentType,
  context,
  editable,
  onChanged,
}: {
  documentType: string
  context: DocumentContext | undefined
  editable: boolean
  onChanged: () => void
}) {
  const [what, setWhat] = useState(context?.what_it_is ?? '')
  const [who, setWho] = useState(context?.who_reads_it ?? '')
  const [how, setHow] = useState(context?.how_we_write ?? '')
  // Opening a draft forks a new version and refetches — the boxes must follow
  // it. Compared against the last value the server gave, so a box being typed
  // into is not wiped by an unrelated refetch.
  const [fromServer, setFromServer] = useState(context)
  if (context !== fromServer) {
    setFromServer(context)
    setWhat(context?.what_it_is ?? '')
    setWho(context?.who_reads_it ?? '')
    setHow(context?.how_we_write ?? '')
  }
  const save = useMutation({
    mutationFn: () =>
      api.writeDocumentContext(documentType, {
        preflight: false,
        what_it_is: what,
        who_reads_it: who,
        how_we_write: how,
      } as never),
    onSuccess: onChanged,
  })

  const dirty =
    what !== (context?.what_it_is ?? '') ||
    who !== (context?.who_reads_it ?? '') ||
    how !== (context?.how_we_write ?? '')

  // **The hint sits above the box, and the example is separate from it.**
  // Both used to be one italic line *below* the field, so an officer met an
  // empty box first and found the help only after scrolling past it — and the
  // line did two jobs at once, telling him what to write and showing him one.
  // Short sentences, ordinary words: this is the first thing a new officer
  // reads on this screen and it must not be the thing that stops him.
  const boxes: {
    label: string
    hint: string
    example: string
    value: string
    set: (v: string) => void
    testid: string
  }[] = [
    {
      label: 'What this document is',
      hint: 'What the paper is, and what it gets done.',
      example:
        'A noting that puts a question to the head of the unit and gets his decision on it.',
      value: what,
      set: setWhat,
      testid: 'about-what',
    },
    {
      label: 'Who reads it, and what they do with it',
      hint: 'Who signs it, and what they do next.',
      example:
        'The CGM writes his order on the same sheet, so the question must have a yes or no answer.',
      value: who,
      set: setWho,
      testid: 'about-who',
    },
    {
      label: 'How this office writes',
      hint: 'Your rules for the words.',
      example: 'Third person. No heading. Subject line carries the name and ticket number.',
      value: how,
      set: setHow,
      testid: 'about-how',
    },
  ]

  const empty = !what.trim() && !who.trim() && !how.trim()

  return (
    <section className="card" data-testid="about-this-document">
      <header>
        <h2>About this document</h2>
        <span className="small muted">
          Sent with every drafting call for <span className="mono">{documentType}</span>
        </span>
      </header>
      <div className="body">
        <p className="small faint">
          Write in Hindi or English — whichever you think in. The model reads this when it
          drafts; it is never printed on the document. Leave a box empty if you have nothing to
          say.
        </p>
        {boxes.map((box) => (
          <div key={box.testid} className="about-field">
            <label className="small" htmlFor={box.testid}>
              {box.label}
            </label>
            <p className="small faint hint">{box.hint}</p>
            <p className="small faint example">For example — “{box.example}”</p>
            <GrowingTextarea
              id={box.testid}
              value={box.value}
              disabled={!editable}
              testid={box.testid}
              onChange={box.set}
            />
            {editable ? (
              <Polish text={box.value} mode="instruction" onAccept={box.set} />
            ) : null}
          </div>
        ))}
        {editable ? (
          <button
            type="button"
            className="btn"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        ) : null}
        {empty && !editable ? (
          <p className="small warn-text">
            Nothing written yet. Every document generated from this template is drafted without
            the model being told what the document is for.
          </p>
        ) : null}
        <ErrorEnvelope error={save.error} />
      </div>
    </section>
  )
}

/**
 * What this draft changes — **at the publish step, not while authoring**.
 *
 * It was removed from the middle of this screen on 2 September 2026 while D01
 * was being rebuilt block by block: it reports one row per edit, so a rebuild
 * buried the paragraphs under its own changelog. The answer was never to
 * delete it. It is a pre-publish read — the question *what am I about to
 * publish* has one moment, and this is that moment.
 *
 * `statutory_moved` is the line that matters. Statutory text differing from
 * the published version is only ever legitimate when the skeleton on disk
 * moved too, and an administrator about to publish is the last person who can
 * still catch it.
 */
function WhatThisDraftChanges({ documentType }: { documentType: string }) {
  const diff = useQuery({
    queryKey: ['template-diff', documentType],
    queryFn: () => api.getTemplateDiff(documentType),
  })
  if (!diff.data) return null
  const changes = diff.data.changes ?? []
  return (
    <div data-testid="what-this-draft-changes" style={{ marginBottom: 12 }}>
      <h3 className="small">
        What this draft changes{' '}
        <span className="small faint mono">
          v{diff.data.from_version ?? '—'} → v{diff.data.to_version}
        </span>
      </h3>
      {diff.data.statutory_moved ? (
        <p className="warn-text small" role="alert">
          Statutory text differs from the published version. That is only ever legitimate when
          the skeleton on disk moved too.
        </p>
      ) : null}
      {changes.length === 0 ? (
        <p className="small muted">Nothing yet.</p>
      ) : (
        <ul className="changes">
          {changes.map((change, index) => (
            <li key={index}>
              <span className="chip neutral">{change.kind}</span>{' '}
              <span className="mono">{change.key}</span> — {change.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Empty the draft, and start the template from a blank page.
 *
 * The office an administrator is writing for may write a noting that looks
 * nothing like the skeleton it was seeded with, and the only way to a blank
 * page was pressing Remove once per paragraph — twenty times on D01, selecting
 * the wrong row on half of them, because the panel re-points at whatever moved
 * up. It is one decision and it is now one act.
 *
 * **Everything goes, `[S]` included** — ruled on 5 September 2026. The lock is on
 * the words, not on the paragraph's existence: a paraphrased statute is a
 * different statute, and an absent one is an editorial decision about a
 * document. The publish gate agrees; every statutory rule there guards the text
 * and none requires a statutory block to be present.
 *
 * **So the confirmation says what the way back is**, because there is no
 * statutory entry in the palette: putting one back means *Reload from skeleton*,
 * which restores the whole template rather than the paragraph. A screen that
 * offered a clear without saying that would be a one-way door with no sign on
 * it.
 *
 * Confirmed rather than immediate. Not because it is dangerous — it is not —
 * but because "start again" is a decision, and a decision taken by a misclick
 * is the one an administrator cannot tell from a bug.
 */
function StartAgain({
  documentType,
  blocks,
  statutory,
  onChanged,
}: {
  documentType: string
  blocks: number
  statutory: number
  onChanged: () => void
}) {
  const [asking, setAsking] = useState(false)
  const clear = useMutation({
    mutationFn: () => api.clearTemplateBlocks(documentType),
    onSuccess: () => {
      setAsking(false)
      onChanged()
    },
  })

  if (!asking) {
    return (
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
        <button
          type="button"
          className="quiet"
          onClick={() => setAsking(true)}
          data-testid="start-again"
        >
          Start from a blank page
        </button>
      </div>
    )
  }
  return (
    <div className="para-note" data-testid="start-again-confirm">
      <p className="small">
        This takes out all <strong>{blocks}</strong> {blocks === 1 ? 'paragraph' : 'paragraphs'}{' '}
        and leaves you writing this template from nothing.
      </p>
      {statutory ? (
        <p className="small">
          That includes {statutory} statutory{' '}
          {statutory === 1 ? 'paragraph' : 'paragraphs'}. <strong>There is no way to add
          statutory text back from the palette</strong> — it comes from the rule corpus through
          the Template Library, so putting one back means <em>Reload from skeleton</em>, which
          restores the whole template rather than the one paragraph.
        </p>
      ) : null}
      <p className="small faint">
        Nothing is lost for good. <em>Discard this draft</em>, at the foot of the page, puts
        the published version back.
      </p>
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={clear.isPending}
          onClick={() => clear.mutate()}
          data-testid="start-again-confirm-button"
        >
          {clear.isPending ? 'Clearing…' : `Clear all ${blocks} and start again`}
        </button>
        <button type="button" className="quiet" onClick={() => setAsking(false)}>
          Keep them
        </button>
      </div>
      <ErrorEnvelope error={clear.error} />
    </div>
  )
}

function Blocks({
  detail,
  editable,
  onChanged,
  language,
  warningsByBlock,
}: {
  detail: TemplateDetail
  editable: boolean
  onChanged: () => void
  language: 'hi' | 'en'
  warningsByBlock: Map<number, string[]>
}) {
  // The case every block is tried against. Chosen once, at the top, because
  // an instruction is judged by what it produces — and judging it means
  // reading a real paragraph off real papers, not imagining one.
  const [tryOnCase, setTryOnCase] = useState('')
  const cases = useQuery({ queryKey: ['cases'], queryFn: () => api.listCases() })

  // Which paragraph the panel is about. Held here rather than in the split
  // view, because the panel sits outside it — the two panes and the panel are
  // three views of one selection, and a selection owned by one of them is a
  // selection the other two can disagree with.
  const [selected, setSelected] = useState<number | null>(null)
  const blocks = (detail.blocks ?? []).filter((block) => block.renders)
  // Read back off the fresh detail every render, never held: a paragraph that
  // is removed, or renumbered by an insert above it, must not leave the panel
  // editing an order that now means a different paragraph.
  const selectedBlock = blocks.find((block) => block.order === selected) ?? null

  return (
    <section className="card">
      <header>
        <h2>The document, block by block</h2>
        {editable ? (
          <label
            className="small muted"
            style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}
          >
            Try against
            <select
              value={tryOnCase}
              onChange={(e) => setTryOnCase(e.target.value)}
              data-testid="try-case"
            >
              <option value="">choose a case…</option>
              {(cases.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.file_number ?? 'Not yet numbered'}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </header>
      <div className="body blocks">
        {editable ? (
          <WholeDocument documentType={detail.document_type} tryOnCase={tryOnCase} />
        ) : null}
        {/* Beside try-the-whole-document, where the case is already chosen —
            this adds "and here is what the office actually wrote for it." */}
        {editable ? (
          <CompareWithReal
            documentType={detail.document_type}
            tryOnCase={tryOnCase}
            editable={editable}
          />
        ) : null}
        {editable && blocks.length > 0 ? (
          <StartAgain
            documentType={detail.document_type}
            blocks={blocks.length}
            statutory={blocks.filter((b) => b.provenance === 'S').length}
            onChanged={onChanged}
          />
        ) : null}

        <SplitEditor
          detail={detail}
          editable={editable}
          language={language}
          tryOnCase={tryOnCase}
          onChanged={onChanged}
          selected={selected}
          onSelect={setSelected}
          warningsByBlock={warningsByBlock}
        />

        {/* **The panel, and it is the only place a paragraph changes.** One
            paragraph at a time, because the officer selected one: a list that
            offers every control on every row is a list where the wrong row is
            edited, and both panes above are showing which row this is. */}
        {selectedBlock ? (
          <Block
            key={selectedBlock.order}
            documentType={detail.document_type}
            block={selectedBlock}
            editable={editable}
            onChanged={onChanged}
            tryOnCase={tryOnCase}
            paragraphKinds={detail.paragraph_kinds ?? []}
            paragraphStyles={detail.paragraph_styles ?? []}
            onMoved={setSelected}
            language={language}
            warnings={warningsByBlock.get(selectedBlock.order) ?? []}
          />
        ) : blocks.length > 0 ? (
          <p className="small muted" data-testid="nothing-selected">
            Choose a paragraph in either pane to see what it is and change it.
          </p>
        ) : null}

        {editable && blocks.length > 0 ? (
          <Palette
            documentType={detail.document_type}
            after={selected ?? blocks[blocks.length - 1]?.order ?? null}
            onChanged={onChanged}
          />
        ) : null}
      </div>
    </section>
  )
}

/**
 * The polish button — a small tool that assists an officer writing.
 *
 * Not a writing assistant: told to *improve* a sentence a model makes it longer
 * and more polite, which is worse. An instruction improves by getting more
 * specific, more ordered and shorter; the office's own text improves by being
 * left almost alone. Two modes, one button.
 *
 * It returns a **proposal** and writes nothing. The officer reads the diff and
 * accepts or rejects, which is rule 2 and the same posture as every other AI
 * change in this product.
 */
function Polish({
  text,
  mode,
  onAccept,
  evidence,
}: {
  text: string
  mode: 'instruction' | 'text'
  onAccept: (next: string) => void
  evidence?: { caseFileNumber: string; lastOutput: string } | null
}) {
  const [proposal, setProposal] = useState<PolishProposal | null>(null)
  const polish = useMutation({
    mutationFn: () =>
      api.polishWriting({
        preflight: false,
        text,
        mode,
        case_file_number: evidence?.caseFileNumber ?? null,
        last_output: evidence?.lastOutput ?? null,
      } as never),
    onSuccess: setProposal,
  })

  return (
    <div className="polish">
      <button
        type="button"
        className="quiet"
        disabled={!text.trim() || polish.isPending}
        onClick={() => polish.mutate()}
        title={
          mode === 'instruction'
            ? 'Suggest a sharper version of this instruction. Nothing is written until you accept it.'
            : "Suggest a small improvement to the office's own words. Nothing is written until you accept it."
        }
      >
        {/* **"Suggest a wording", not "Polish".** Polish sounds cosmetic and
            sounds like it happens to the text; what the button does is come
            back with a proposal the officer accepts or rejects. The label
            should say which of those two things it is. */}
        {polish.isPending ? 'Thinking…' : 'Suggest a wording'}
      </button>
      {evidence ? (
        <span className="small faint"> — against {evidence.caseFileNumber}</span>
      ) : null}
      {proposal ? (
        proposal.old === proposal.new ? (
          <p className="small muted" style={{ marginTop: 6 }}>
            No change suggested. {proposal.reason}
          </p>
        ) : (
          <div className="proposal" style={{ marginTop: 8 }}>
            {/* Current struck, proposed green — the diff card's own shape, so a
                polish reads exactly like every other AI change in the product. */}
            <del>{proposal.old}</del>
            <ins>{proposal.new}</ins>
            {proposal.reason ? <p className="small muted">{proposal.reason}</p> : null}
            <div className="row" style={{ marginTop: 6 }}>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  onAccept(proposal.new)
                  setProposal(null)
                }}
              >
                Accept
              </button>
              <button type="button" className="quiet" onClick={() => setProposal(null)}>
                Reject
              </button>
            </div>
          </div>
        )
      ) : null}
      <ErrorEnvelope error={polish.error} />
    </div>
  )
}

/**
 * Try one block against a real case, from where it is being authored.
 *
 * The loop the editor never had. Authoring an instruction and then publishing,
 * opening a case, generating a document and reading the result is four screens
 * and an irreversible publish to test one sentence — so nobody tested one, and
 * D01's instructions stayed as we had written them from reading samples, never
 * corrected by anyone in the section.
 *
 * Nothing is written. The provider IS called for a section the model writes,
 * because a preview that skipped it would test everything except the thing
 * being authored — and that call is real, so it appears in the provider log
 * like any other.
 */
/** The languages a document exists in, and how to start another.
 *
 * **Hindi is the source and always sits first.** Everything in the library is
 * Hindi because of where the project started, not because the organisation
 * works in Hindi — most of the nine factories will work in English, so the
 * English versions are what most of them will actually use.
 *
 * They are separate templates: separate blocks, separate instructions,
 * separate version history, separate publish cycle. So an officer may make the
 * English *structurally* different — a paragraph fewer, a heading the Hindi
 * does not have — rather than merely differently worded.
 */
const LANGUAGE_NAMES: Record<string, string> = { hi: 'Hindi', en: 'English' }

function LanguageTabs({
  detail,
  language,
  onPick,
  onChanged,
}: {
  detail: TemplateDetail
  language: string
  onPick: (language: 'hi' | 'en') => void
  onChanged: () => void
}) {
  const add = useMutation({
    mutationFn: () =>
      api.addTemplateLanguage(detail.document_type, { preflight: false, language: 'en' }),
    onSuccess: () => {
      onPick('en')
      onChanged()
    },
  })

  const languages = detail.languages ?? ['hi']
  const canAddEnglish = !languages.includes('en')

  return (
    <div className="language-tabs" data-testid="language-tabs">
      <nav className="tabs">
        {languages.map((code) => (
          <button
            key={code}
            type="button"
            className={code === language ? 'active' : ''}
            onClick={() => onPick(code === 'en' ? 'en' : 'hi')}
            data-testid={`language-${code}`}
          >
            {LANGUAGE_NAMES[code] ?? code}
            {code === 'hi' ? <span className="small faint"> · the source</span> : null}
          </button>
        ))}
        {canAddEnglish ? (
          <button
            type="button"
            className="quiet"
            disabled={add.isPending}
            onClick={() => add.mutate()}
            data-testid="add-language"
            title="Start the English version, seeded from the Hindi. From then on it is yours."
          >
            {add.isPending ? 'Starting…' : '+ Add English'}
          </button>
        ) : null}
      </nav>
      {language !== 'hi' ? (
        <p className="small faint">
          This is a <strong>separate template</strong>, seeded once from the Hindi and yours
          from then on. It is never re-translated, and it may differ from the Hindi in structure
          as well as wording. The pre-issue checks are shared &mdash; a check is about facts,
          and is true or false whatever language the document is in.
        </p>
      ) : null}
      <ErrorEnvelope error={add.error} />
    </div>
  )
}

/** What each kind of difference means, and where it sends the officer.
 *
 * Carried on the row rather than left in a legend: the point of classifying a
 * difference is that each kind is acted on somewhere different, and a reader
 * holding four definitions in their head while scanning twenty rows will act on
 * the wrong one.
 */
const DIFFERENCE_KINDS: Record<string, { label: string; tone: string }> = {
  template: { label: 'the template', tone: 'bad' },
  instruction: { label: 'the instruction', tone: 'warn' },
  record: { label: 'the record', tone: 'neutral' },
  unclear: { label: 'unclear', tone: 'neutral' },
}

/**
 * Judge this draft against a document the office actually issued.
 *
 * Point 9, and **the strongest signal in the product.** Every other check
 * measures a document against a rule we wrote or an opinion somebody holds.
 * This measures it against what the office actually issued, signed and filed —
 * there is no appeal from that.
 *
 * It is the comparison that was done by hand on 30 Aug 2026 and found eight
 * divergences between the real noting and the library's D01, including the file
 * number's true shape, which weeks of reading the same skeleton had missed.
 *
 * **It proposes nothing.** It reports the difference and what kind it is; the
 * officer edits. A tool that both finds a problem and applies its own answer
 * removes the judgement this product exists to protect.
 */
function CompareWithReal({
  documentType,
  tryOnCase,
  editable,
}: {
  documentType: string
  tryOnCase: string
  editable: boolean
}) {
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const compare = useMutation({
    mutationFn: () => api.compareAgainstReal(documentType, tryOnCase, file as File),
  })

  if (!editable) return null
  if (!open) {
    return (
      <button
        type="button"
        className="quiet"
        onClick={() => setOpen(true)}
        data-testid="compare"
        disabled={!tryOnCase}
        title={
          tryOnCase
            ? "Put this draft's output beside the document the office actually issued"
            : 'Choose a case above first'
        }
      >
        Compare with the real document
      </button>
    )
  }

  const result = compare.data
  return (
    <section className="card" data-testid="compare-panel">
      <header>
        <h2>Against what the office actually wrote</h2>
      </header>
      <div className="body">
        <p className="small muted">
          Upload the document this office <strong>actually issued</strong> for the case chosen
          above. It generates from this draft, sets the two side by side, and says what kind
          each difference is — so you know whether to change the template, the instruction, or
          the record.
        </p>
        <p className="small faint">
          It must be the document for <em>this</em> matter. A real noting about a different
          employee makes every difference noise. Nothing is stored; it is read, compared and
          dropped.
        </p>

        <label className="small" style={{ display: 'block', marginTop: 10 }}>
          The office&rsquo;s own document — a Word file
          <input
            type="file"
            accept=".docx,.txt,text/plain"
            data-testid="compare-file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>

        <div className="row" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={!file || compare.isPending}
            onClick={() => compare.mutate()}
          >
            {compare.isPending ? 'Comparing…' : 'Compare them'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>

        {result
          ? ((differences: DifferenceView[]) => (
              <div className="comparison" data-testid="comparison" style={{ marginTop: 14 }}>
                <p className="small muted">
                  Against <span className="mono">{result.case_file_number}</span> ·{' '}
                  <strong>{differences.length}</strong> difference
                  {differences.length === 1 ? '' : 's'} · {result.matched} passage
                  {result.matched === 1 ? '' : 's'} matched
                </p>

                {/* The counts first, because the shape of the answer is the answer:
                mostly `instruction` is a drafting problem, mostly `template` is
                a structural one, and mostly `record` is neither. */}
                <div className="row small" style={{ gap: 14, flexWrap: 'wrap' }}>
                  {Object.entries(result.by_kind ?? {})
                    .filter(([, n]) => (n as number) > 0)
                    .map(([kind, n]) => (
                      <span
                        key={kind}
                        className={`chip ${DIFFERENCE_KINDS[kind]?.tone ?? 'neutral'}`}
                      >
                        {String(n)} · {DIFFERENCE_KINDS[kind]?.label ?? kind}
                      </span>
                    ))}
                </div>

                {differences.length === 0 ? (
                  <p className="small good-text" style={{ marginTop: 10 }}>
                    Nothing worth reporting. Read that as &ldquo;no difference this found worth
                    an officer&rsquo;s time&rdquo;, not as proof the two are identical.
                  </p>
                ) : null}

                {differences.map((d: DifferenceView, i: number) => (
                  <div key={i} className="difference" data-testid={`difference-${i}`}>
                    <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                      <span className={`chip ${DIFFERENCE_KINDS[d.kind]?.tone ?? 'neutral'}`}>
                        {DIFFERENCE_KINDS[d.kind]?.label ?? d.kind}
                      </span>
                      <span className="small faint mono">{d.where}</span>
                    </div>
                    <div className="sides">
                      <div>
                        <span className="small faint">We produced</span>
                        <pre className="deva scaffold">{d.ours || '⟦nothing⟧'}</pre>
                      </div>
                      <div>
                        <span className="small faint">The office wrote</span>
                        <pre className="deva scaffold">{d.theirs || '⟦nothing⟧'}</pre>
                      </div>
                    </div>
                    {d.why ? <p className="small muted">{d.why}</p> : null}
                    {d.means ? <p className="small faint">{d.means}</p> : null}
                  </div>
                ))}

                {result.notes ? (
                  <p className="small warn-text" style={{ marginTop: 10 }}>
                    {result.notes}
                  </p>
                ) : null}
              </div>
            ))(result.differences ?? [])
          : null}
        <ErrorEnvelope error={compare.error} />
      </div>
    </section>
  )
}

/**
 * Try the whole document against the chosen case.
 *
 * At the top of the document rather than on a block, which is where Archit put
 * it: *"it is try the whole document, so writing it on a block doesn't make
 * sense."*
 *
 * It is the only way to catch a fault that belongs to no single block. The one
 * that argued for it: D01's opening paragraph restated the subject line two
 * blocks above it, and neither block is wrong read alone.
 *
 * Nothing is written — no document, no version, no register number. A preview
 * that left a document behind would burn a serial the register can never
 * reuse.
 */
function WholeDocument({
  documentType,
  tryOnCase,
}: {
  documentType: string
  tryOnCase: string
}) {
  const [open, setOpen] = useState(false)
  const run = useMutation({
    mutationFn: () =>
      api.previewTemplateDocument(documentType, { preflight: false, case_id: tryOnCase }),
    onSuccess: () => setOpen(true),
  })

  return (
    <div className="whole-document" style={{ marginBottom: 12 }}>
      <button
        type="button"
        className="quiet"
        disabled={!tryOnCase || run.isPending}
        onClick={() => run.mutate()}
        data-testid="try-whole-document"
        title={
          tryOnCase
            ? 'Generate the whole document from this draft, against the chosen case. Nothing is written.'
            : 'Choose a case above first'
        }
      >
        {run.isPending ? 'Drafting the whole document…' : 'Try the whole document'}
      </button>
      <span className="small faint">
        {' '}
        — the only way to see a fault that belongs to no single block, like one paragraph
        restating another
      </span>
      {run.data && open ? (
        <div className="preview" style={{ marginTop: 8 }} data-testid="whole-document-preview">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="small muted">
              {documentType} against {run.data.case_file_number} — nothing written
            </span>
            <button type="button" className="quiet" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          {(run.data.blocks ?? []).map((row) => (
            <pre key={row.order} className="deva scaffold" style={{ marginBottom: 4 }}>
              {row.text || '⟦nothing rendered⟧'}
            </pre>
          ))}
          {(run.data.unresolved ?? []).map((chip: UnresolvedSlotView) => (
            <p key={chip.slot} className="small warn-text">
              <span className="mono">{chip.slot}</span> — {chip.reason}
            </p>
          ))}
        </div>
      ) : null}
      <ErrorEnvelope error={run.error} />
    </div>
  )
}

function TryIt({
  block,
  tryOnCase,
  preview,
  editable,
  history,
}: {
  block: TemplateBlockView
  tryOnCase: string
  preview: {
    mutate: () => void
    isPending: boolean
    data?: BlockPreview
    error: unknown
  }
  editable: boolean
  history: BlockPreview[]
}) {
  const [showPrompt, setShowPrompt] = useState(false)
  if (!editable) return null
  const latest = preview.data
  const earlier = history.filter((row) => row !== latest)

  return (
    <div className="try-it" style={{ marginTop: 10 }}>
      <button
        type="button"
        className="quiet"
        disabled={!tryOnCase || preview.isPending}
        onClick={() => preview.mutate()}
        data-testid={`try-${block.order}`}
        title={
          tryOnCase
            ? 'Run this block against the chosen case, using what is typed above — saved or not. Nothing is written.'
            : 'Choose a case at the top of this section first'
        }
      >
        {preview.isPending ? 'Drafting…' : 'Try it on this case'}
      </button>
      {!tryOnCase ? (
        <span className="small faint"> — choose a case at the top first</span>
      ) : null}
      {latest ? (
        <div
          className="preview"
          style={{ marginTop: 8 }}
          data-testid={`preview-${block.order}`}
        >
          <div className="small muted">
            {latest.drafted_by_model
              ? `Written by the model for ${latest.case_file_number}`
              : `Assembled from the record for ${latest.case_file_number} — no model involved`}
          </div>
          <pre className="deva scaffold">{latest.text || '⟦nothing rendered⟧'}</pre>
          {/* A refusal is the most important thing on this panel, not a
              footnote to it. It used to render `small faint` grey. */}
          {(latest.unresolved ?? []).map((chip: UnresolvedSlotView) => (
            <p key={chip.slot} className="small warn-text" data-testid="preview-refusal">
              <span className="mono">{chip.slot}</span> — {chip.reason}
            </p>
          ))}
          {latest.prompt ? (
            <>
              <button
                type="button"
                className="quiet"
                onClick={() => setShowPrompt((open) => !open)}
                data-testid={`prompt-${block.order}`}
              >
                {showPrompt ? 'Hide what the model was told' : 'Show what the model was told'}
              </button>
              {showPrompt ? (
                <>
                  <pre className="scaffold" style={{ whiteSpace: 'pre-wrap' }}>
                    {latest.prompt}
                  </pre>
                  <p className="small faint">
                    This is the prompt as sent — the instruction, the case record, the papers
                    and the standing rules. Two faults were found only by reading it: a document
                    asked for in English whose prompt was two English sentences against two
                    thousand characters of Hindi, and a heading this office does not use,
                    written because our own instruction asked for one.
                  </p>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {/* The last few tries, so instruction A's output can be compared against
          instruction B's rather than remembered. Cheap, and its value is
          unproven — if it serves no purpose in use, drop it then. */}
      {earlier.length ? (
        <details style={{ marginTop: 6 }}>
          <summary className="small muted">Earlier tries ({earlier.length})</summary>
          {earlier.map((row, index) => (
            <div key={index} className="preview" style={{ marginTop: 6, opacity: 0.75 }}>
              <div className="small faint">{row.instruction || '⟦no instruction⟧'}</div>
              <pre className="deva scaffold">{row.text || '⟦nothing rendered⟧'}</pre>
            </div>
          ))}
        </details>
      ) : null}
      <ErrorEnvelope error={preview.error} />
    </div>
  )
}

function Block({
  documentType,
  block,
  editable,
  onChanged,
  tryOnCase,
  paragraphKinds,
  paragraphStyles,
  onMoved,
  language,
  warnings = [],
}: {
  documentType: string
  block: TemplateBlockView
  editable: boolean
  onChanged: () => void
  tryOnCase: string
  paragraphKinds: ParagraphKindView[]
  paragraphStyles: ParagraphStyleView[]
  onMoved?: (order: number) => void
  language: 'hi' | 'en'
  warnings?: string[]
}) {
  const [text, setText] = useState(block.text_hi)
  const [instruction, setInstruction] = useState(block.instruction ?? '')
  // **Reset when the SERVER's value changes, not on every refetch.** A block is
  // keyed by its order and a move renumbers every block after it, so without
  // this the officer's unsaved text stays behind on the position while the
  // block moves out from under it — the textarea then shows one block's words
  // over another block's heading.
  //
  // Comparing against the last value the server gave, rather than resetting on
  // any refetch, is what keeps a half-typed instruction alive when something
  // else on the same block is saved — choosing a section type must not wipe
  // what is being written above it.
  const [fromServer, setFromServer] = useState({
    text: block.text_hi,
    instruction: block.instruction ?? '',
  })
  if (
    block.text_hi !== fromServer.text ||
    (block.instruction ?? '') !== fromServer.instruction
  ) {
    setFromServer({ text: block.text_hi, instruction: block.instruction ?? '' })
    setText(block.text_hi)
    setInstruction(block.instruction ?? '')
  }
  // The last few tries, newest first. Held on the block rather than in the
  // query cache because it is a working record of one authoring session, not
  // server state — reloading the template should lose it.
  const [tries, setTries] = useState<BlockPreview[]>([])
  const preview = useMutation({
    mutationFn: () =>
      api.previewTemplateBlock(documentType, {
        preflight: false,
        order: block.order,
        case_id: tryOnCase,
        language: 'hi',
        // **What is being typed, not what was last saved.** The save that used
        // to be required here was friction introduced by accident, and it is
        // the friction that stopped anyone testing a sentence at all.
        instruction,
        text_hi: text,
      } as never),
    onSuccess: (result: BlockPreview) => setTries((rows) => [result, ...rows].slice(0, 4)),
  })
  const remove = useMutation({
    mutationFn: () =>
      api.removeTemplateBlock(documentType, { preflight: false, order: block.order }),
    onSuccess: onChanged,
  })
  const split = useMutation({
    mutationFn: (at: number) =>
      api.splitTemplateBlock(documentType, { preflight: false, order: block.order, at }),
    onSuccess: onChanged,
  })
  const move = useMutation({
    mutationFn: (direction: 'up' | 'down') =>
      api
        .moveTemplateBlock(documentType, { preflight: false, order: block.order, direction })
        .then((detail) => {
          // **The selection follows the paragraph — found, not assumed.**
          //
          // A block is identified by its `order`, and a move swaps two of them,
          // so selecting paragraph 3 and pressing ↓ left both panes
          // highlighting row 3 — now the paragraph that swapped into it — with
          // every control pointed at that one instead.
          //
          // The first fix computed the new order as ±1, which assumed the move
          // happened. Pressing ↑ on the first paragraph is a legitimate no-op:
          // it selected order 0, which matches nothing, so the whole editing
          // panel vanished. And a template whose last paragraph is followed by
          // a non-rendering notes block — 14 of the 19 skeletons have one —
          // moves further than the eye can see.
          //
          // So the answer comes off the server's own reply: find where this
          // paragraph actually is now.
          const moved = (detail.blocks ?? []).find(
            (b) => b.text_hi === block.text_hi && b.part === block.part,
          )
          if (moved) onMoved?.(moved.order)
          return detail
        }),
    onSuccess: onChanged,
  })
  const save = useMutation({
    mutationFn: (body: { order: number; text_hi?: string; instruction?: string }) =>
      api.editTemplateBlock(documentType, { preflight: false, ...body }),
    onSuccess: onChanged,
  })
  const provenance = useMutation({
    mutationFn: (action: ProvenanceAction) =>
      api.setBlockProvenance(documentType, { preflight: false, order: block.order, action }),
    onSuccess: onChanged,
  })

  const statutory = block.provenance === 'S'
  const caseBlock = block.provenance === 'C'
  // The organisation's text wins at render, so editing the copy held on a
  // template would be a change that silently did not happen. The block is shown
  // as what it is and the edit is offered where it takes effect.
  const governedByOrgBlock = Boolean(block.org_block_role)
  const latest = tries[0]
  const dirty = text !== block.text_hi || instruction !== (block.instruction ?? '')

  const moveButtons = editable ? (
    <>
      {/* Not on statutory text, which the domain refuses — but on everything
          else, including a block rendered from an organisation block. That one
          has no editor of its own, so without this a template could carry a
          line this office does not write and no one could take it out. */}
      {!statutory ? (
        <button
          type="button"
          className="quiet"
          title="Remove this block from the draft. The library reclaims a skeleton block on the next reload, and says so."
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          data-testid={`remove-${block.order}`}
        >
          Remove
        </button>
      ) : null}
      <button
        type="button"
        className="quiet"
        title="Move this block one position earlier"
        onClick={() => move.mutate('up')}
        disabled={move.isPending}
        data-testid={`up-${block.order}`}
      >
        ↑
      </button>
      <button
        type="button"
        className="quiet"
        title="Move this block one position later"
        onClick={() => move.mutate('down')}
        disabled={move.isPending}
        data-testid={`down-${block.order}`}
      >
        ↓
      </button>
    </>
  ) : null

  return (
    <article className="block" data-provenance={block.provenance ?? 'none'}>
      {/* Every mutation on this block, surfaced. A Save that 500s with nothing
          on screen is indistinguishable from success, and the officer's work
          vanishes — that is precisely how the stuck D08/D14 drafts destroyed
          edits for days (audit of 02/09/2026, F-12). The server's refusals
          carry the rule and the remedy; hiding them was the defect. */}
      <ErrorEnvelope
        error={
          save.error ??
          remove.error ??
          move.error ??
          split.error ??
          provenance.error ??
          preview.error
        }
      />
      <header>
        <span className="mono faint">{block.order}</span>
        <LayerBadge provenance={block.provenance} />
        {block.learned_why ? (
          <span className="chip neutral" title="Proposed by reading the office's own documents">
            read from documents
          </span>
        ) : null}
        {block.org_block_role ? (
          <span className="chip neutral" title="Rendered from the organisation's standing text">
            org · {block.org_block_role}
          </span>
        ) : null}
        {(block.citations ?? []).map((citation) => (
          <span key={citation} className="citation-static mono">
            {citation}
          </span>
        ))}
        {/* A statutory block's TEXT is locked, not its position. Refusing to
            move one would make a template with a misplaced [S] block
            unfixable by anybody. */}
        {moveButtons}
      </header>

      {governedByOrgBlock && !statutory ? (
        <>
          <pre className="deva scaffold">{block.text_hi}</pre>
          <p className="small faint" data-testid="org-governed">
            Rendered from the organisation block{' '}
            <span className="mono">{block.org_block_role}</span>, which is authored once and
            followed by every template. Editing the copy held here would change nothing that
            issues — edit it under <Link to="/templates">Organisation blocks</Link>.
          </p>
        </>
      ) : statutory ? (
        /* No editing affordance at all. Not a disabled field an officer might
           read as a bug — a lock and a sentence saying where this text comes
           from. The API refuses the edit too; this is the half that means
           nobody tries. */
        <>
          <pre className="deva locked" data-testid="statutory-block">
            {block.text_hi}
          </pre>
          <p className="small faint" data-testid="statutory-lock">
            <span className="unverified">LOCKED · STATUTORY</span> Reproduced verbatim from the
            rules. It changes in the Template Library and arrives through the loader — never
            through an edit, by anyone. A paraphrased statute is a different statute.
          </p>
        </>
      ) : (
        <>
          {/* **Both fields, on every block that is not statutory.** The subject
              line is neither purely the office's words nor purely the model's —
              श्री … at the front and … के संबंध में । at the back are identical
              in every real example and only the middle changes. The officer
              decides what goes in each field; a frame is simply something an
              instruction can describe. */}
          <label className="small">
            {caseBlock
              ? 'The words that stand on the page — the frame, if this section has one'
              : 'The line, exactly as it appears on every document'}
            <textarea
              className="deva"
              rows={Math.min(10, Math.max(2, (text || '').split('\n').length))}
              value={text}
              disabled={!editable}
              data-testid={`text-${block.order}`}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
          {editable ? (
            <Polish
              text={text}
              mode="text"
              onAccept={setText}
              evidence={
                latest
                  ? { caseFileNumber: latest.case_file_number, lastOutput: latest.text }
                  : null
              }
            />
          ) : null}

          <label className="small" style={{ marginTop: 8, display: 'block' }}>
            What the model is told about this section — in your own words
            <textarea
              rows={3}
              value={instruction}
              disabled={!editable}
              data-testid={`instruction-${block.order}`}
              onChange={(event) => setInstruction(event.target.value)}
            />
          </label>
          {editable ? (
            <Polish
              text={instruction}
              mode="instruction"
              onAccept={setInstruction}
              evidence={
                latest
                  ? { caseFileNumber: latest.case_file_number, lastOutput: latest.text }
                  : null
              }
            />
          ) : null}
          <p className="small faint">
            <strong>This instruction is sent word for word</strong> as this section&rsquo;s
            purpose, alongside the case record and the papers. Write it the way you would tell a
            new clerk: what the section must contain, in what order, and where each fact comes
            from. Say what must <em>not</em> appear, too. Leave it empty and the model is given
            the words above and nothing else — the standing rules still forbid inventing a fact
            or citing a rule nobody supplied, and they override anything written here.
          </p>

          {/* **One question, replacing three controls.** The section-type
              picker, the provenance action and the organisation-block role
              each answered a different part of "what is this paragraph?", and
              the third silently overrode the other two. */}
          <div className="section-type" style={{ marginTop: 8 }}>
            <ParagraphKindPicker
              documentType={documentType}
              block={block}
              kinds={paragraphKinds}
              editable={editable}
              onChanged={onChanged}
              language={language}
            />
            <ParagraphStylePicker
              documentType={documentType}
              block={block}
              styles={paragraphStyles}
              editable={editable}
              onChanged={onChanged}
              language={language}
            />
            {/* **The warning, beside the thing that answers it.** These
                used to be four identical banners at the top of the screen
                naming paragraphs by number, so the officer read the problem
                in one place and had to go hunting for the paragraph in
                another. The rule code is not shown: it is our name for the
                check, not his. */}
            {warnings.length ? (
              <div className="para-note" data-testid="block-warnings">
                {warnings.map((message, i) => (
                  <p className="small" key={i}>
                    {message}
                  </p>
                ))}
              </div>
            ) : null}
            <GapsPanel
              documentType={documentType}
              block={block}
              editable={editable}
              onChanged={onChanged}
              language={language}
            />
          </div>

          {editable ? (
            <div className="row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn"
                onClick={() => save.mutate({ order: block.order, text_hi: text, instruction })}
                disabled={save.isPending || !dirty}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              {/* Cut at the caret, so the officer's own cursor decides where
                  the paragraph divides. Nothing trims — trimming would move
                  their cut. */}
              <button
                type="button"
                className="quiet"
                title="Cut this block in two at the cursor, so each half can be worked on separately"
                onClick={() => {
                  const el = document.activeElement as HTMLTextAreaElement | null
                  const at = el && el.selectionStart != null ? el.selectionStart : text.length
                  split.mutate(at)
                }}
                disabled={split.isPending}
              >
                Split at cursor
              </button>
              {block.provenance === 'S/O' ? (
                <>
                  <button type="button" onClick={() => provenance.mutate('confirm-statutory')}>
                    Confirm as statutory
                  </button>
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => provenance.mutate('reclassify-office')}
                  >
                    Reclassify as office text
                  </button>
                </>
              ) : null}
              {block.provenance === 'D' ? (
                <button type="button" onClick={() => provenance.mutate('adopt-as-office')}>
                  Adopt as office text
                </button>
              ) : null}
            </div>
          ) : null}

          <TryIt
            block={block}
            tryOnCase={tryOnCase}
            preview={preview}
            editable={editable}
            history={tries}
          />
        </>
      )}

      {/* **Why this block is what it is.** The alignment is a judgement —
          working out which paragraph of one document corresponds to which of
          another — and real documents do not cooperate. A proposal showing only
          its conclusions can be accepted or thrown away and nothing in between;
          the officer has to be able to correct the reasoning. */}
      {block.learned_why ? (
        <p className="small muted" data-testid={`why-${block.order}`}>
          <strong>Read from the documents:</strong> {block.learned_why}
        </p>
      ) : null}
      {block.provenance === 'D' ? (
        <p className="small warn-text">
          Our own drafting. No AWEIL sample and no prescribed form behind it — it must be
          cleared with the office before real use, and this marker stays until it is adopted.
        </p>
      ) : null}
      {block.provenance === 'S/O' ? (
        <p className="small warn-text">
          Statutory in appearance and unconfirmed. Treated as office text until the Template
          administrator signs it off.
        </p>
      ) : null}
      {block.provenance_settled ? (
        <p className="small faint">
          {String(block.provenance_settled['action'])} by{' '}
          {String(block.provenance_settled['by'])} ·{' '}
          {readDay(String(block.provenance_settled['at']))}
        </p>
      ) : null}
      <ErrorEnvelope error={save.error} />
      <ErrorEnvelope error={move.error} />
      <ErrorEnvelope error={provenance.error} />
    </article>
  )
}

function Slots({
  detail,
  editable,
  onChanged,
}: {
  detail: TemplateDetail
  editable: boolean
  onChanged: () => void
}) {
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [type, setType] = useState('text')
  const [source, setSource] = useState('officer')
  const declare = useMutation({
    mutationFn: () =>
      api.declareTemplateSlot(detail.document_type, {
        preflight: false,
        key,
        label_hi: label || null,
        type,
        source,
        // A list slot is one whose key says so. `parties[]` is a list because
        // the office wrote it with brackets, not because a checkbox was ticked.
        list: key.endsWith('[]'),
        officer_blank: source === 'officer',
        required_at_generation: false,
        remove: false,
      }),
    onSuccess: () => {
      setKey('')
      setLabel('')
      onChanged()
    },
  })

  return (
    <section className="card">
      <header>
        <h2>Declared slots</h2>
      </header>
      <div className="body">
        <p className="small muted">
          Slots are declared as data and <strong>never discovered by scanning text</strong>.
          Adding a row here gives the pre-issue check a new row and the generator a new blank to
          resolve, with no code change anywhere.
        </p>
        <table className="grid">
          <thead>
            <tr>
              <th>Key</th>
              <th>Hindi label</th>
              <th>Type</th>
              <th>Source</th>
              <th>Declared in</th>
            </tr>
          </thead>
          <tbody>
            {(detail.slots ?? []).map((slot) => (
              <tr key={slot.key}>
                <td className="mono">{slot.key}</td>
                <td className="deva">{slot.label_hi ?? '—'}</td>
                <td>{slot.type}</td>
                <td>
                  {slot.source}
                  {slot.officer_blank ? (
                    <span className="chip warn" title="⟦officer blank⟧ — never invented">
                      officer fills
                    </span>
                  ) : null}
                </td>
                <td className="small faint mono">{slot.declared_in ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {editable ? (
          <div className="row form" data-testid="declare-slot">
            <label>
              Key
              <input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="parties[]"
              />
            </label>
            <label>
              Hindi label
              <input value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <label>
              Type
              <select value={type} onChange={(e) => setType(e.target.value)}>
                {['text', 'date', 'number', 'person', 'amount', 'enum', 'list', 'ref'].map(
                  (t) => (
                    <option key={t}>{t}</option>
                  ),
                )}
              </select>
            </label>
            <label>
              Source
              <select value={source} onChange={(e) => setSource(e.target.value)}>
                {['officer', 'case', 'computed', 'generated', 'authority_matrix'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <button type="button" onClick={() => declare.mutate()} disabled={!key.trim()}>
              Declare slot
            </button>
          </div>
        ) : null}
        <ErrorEnvelope error={declare.error} />
      </div>
    </section>
  )
}

function Rules({
  detail,
  editable,
  onChanged,
}: {
  detail: TemplateDetail
  editable: boolean
  onChanged: () => void
}) {
  const named = useQuery({ queryKey: ['named-checks'], queryFn: () => api.listNamedChecks() })
  const [text, setText] = useState('')
  const [severity, setSeverity] = useState<'BLOCK' | 'WARN' | 'STRUCTURAL'>('WARN')
  const [condition, setCondition] = useState<unknown>(null)

  const write = useMutation({
    mutationFn: () =>
      api.writeTemplateCheck(detail.document_type, {
        preflight: false,
        text,
        severity,
        // A rule with a condition is decided by the interpreter; one without is
        // advisory — stored with its severity and shown to the officer to
        // confirm by hand. Not dropped, and not weakened.
        enforcement: condition ? 'predicate' : 'advisory',
        condition: condition ?? null,
        remove: false,
      }),
    onSuccess: () => {
      setText('')
      onChanged()
    },
  })

  return (
    <>
      <section className="card">
        <header>
          <h2>Pre-issue rules</h2>
        </header>
        <div className="body">
          <table className="grid">
            <thead>
              <tr>
                <th>#</th>
                <th>Rule</th>
                <th>Level</th>
                <th>Decided by</th>
                <th>In English</th>
              </tr>
            </thead>
            <tbody>
              {(detail.checks ?? []).map((check, index) => (
                <RuleRow
                  key={`${check.n}-${check.inherited_from ?? ''}-${index}`}
                  check={check}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editable ? (
        <section className="card">
          <header>
            <h2>Write a rule</h2>
          </header>
          <div className="body">
            <label>
              What the rule says
              <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <label>
              Level
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as typeof severity)}
              >
                <option value="BLOCK">BLOCK — the document cannot issue</option>
                <option value="WARN">WARN — flagged, may be passed with a reason</option>
                <option value="STRUCTURAL">STRUCTURAL</option>
              </select>
            </label>
            <ConditionBuilder value={null} onChange={setCondition} />
            <button type="button" onClick={() => write.mutate()} disabled={!text.trim()}>
              Add this rule
            </button>
            <ErrorEnvelope error={write.error} />
          </div>
        </section>
      ) : null}

      <section className="card">
        <header>
          <h2>Code-level checks</h2>
          <span className="small muted">read-only</span>
        </header>
        <div className="body">
          <p className="small muted">
            The rules the twelve operators provably cannot express — sums over collections,
            successors, competence held on a date.{' '}
            <strong>There is no thirteenth operator</strong>; a rule that seems to need one is a
            code-level check with a test.
          </p>
          <ul className="named-checks">
            {(named.data ?? [])
              .filter((check) => (check.applies_to ?? []).includes(detail.document_type))
              .map((check) => (
                <li key={check.id}>
                  <span className="mono">{check.id}</span> <strong>{check.title}</strong>
                  <div className="small muted">{check.explanation}</div>
                  <div className="small faint mono">reads {(check.reads ?? []).join(', ')}</div>
                </li>
              ))}
          </ul>
        </div>
      </section>
    </>
  )
}

function RuleRow({ check }: { check: TemplateCheckView }) {
  return (
    <tr>
      <td className="mono">
        {check.n ?? '—'}
        {check.inherited_from ? (
          <span className="small faint" title="Inherited from a base template's check table">
            {' '}
            ← {check.inherited_from}
          </span>
        ) : null}
      </td>
      <td className="small">{check.text}</td>
      <td>
        <span className={`chip ${check.severity === 'BLOCK' ? 'bad' : 'warn'}`}>
          {check.severity}
        </span>
      </td>
      <td className="small">
        {check.enforcement}
        {check.code_check ? <span className="mono faint"> · {check.code_check}</span> : null}
        {check.origin === 'standing' ? (
          <span className="chip neutral" title="One of the nine rules from real defects">
            standing
          </span>
        ) : null}
      </td>
      <td className="small muted">{check.explain ?? check.rationale ?? ''}</td>
    </tr>
  )
}
